import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MapPin, Lock, Unlock, Clock, Camera, Trophy, Flame, Crown, Zap } from "lucide-react";
import GpsHintButton from "@/components/GpsHintButton";
import HuntZoneMap from "@/components/HuntZoneMap";
import OGMeta from "@/components/OGMeta";
import PhotoUploader from "@/components/PhotoUploader";
import PostClaimHollerNudge from "@/components/PostClaimHollerNudge";
import { PrizeTypeBadge, prizeMeta } from "@/components/PrizeDisplay";
import NextClueChip from "@/components/NextClueChip";

function ClueRow({ clue, idx }) {
  const Icon = clue.unlocked ? Unlock : Lock;
  return (
    <div
      className={`flex gap-4 p-5 border-l-2 ${clue.unlocked ? "border-[#76B82A]" : "border-[#F5F2E8]/20 clue-locked"}`}
      style={{ background: clue.unlocked ? "rgba(118,184,42,0.05)" : "transparent" }}
      data-testid={`clue-row-${idx}`}
    >
      <div>
        <div className="w-10 h-10 flex items-center justify-center border border-current">
          <Icon size={16} />
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="overline">Clue {clue.order} · {clue.type}</span>
          {!clue.unlocked && (
            <span className="font-mono text-[10px] text-[#F5F2E8]/50">
              unlocks in ~{clue.release_offset_minutes}m
            </span>
          )}
        </div>
        <p className={`mt-2 font-mono text-base ${clue.unlocked ? "text-[#F5F2E8]" : "text-[#F5F2E8]/50"}`}>
          {clue.unlocked ? clue.text : "▓▓▓▓▓ ▓▓▓▓▓▓▓▓ ▓▓▓"}
        </p>
      </div>
    </div>
  );
}

export default function DropDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [drop, setDrop] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [showClaim, setShowClaim] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [claimForm, setClaimForm] = useState({
    claim_code: "", story: "", social_handle: "",
    photo_url: "", selfie_url: "",
  });

  // Admin "preview as hunter" mode + sponsor preview mode. Both modes flow
  // through the same plumbing — the backend decides what to allow based on
  // either admin role (?preview=1) or a valid sponsor token (?preview_token=...).
  const isAdminPreview = searchParams.get("preview") === "1";
  const previewToken = searchParams.get("preview_token") || "";
  const isPreview = isAdminPreview || !!previewToken;
  const previewParams = isAdminPreview
    ? { preview: 1 }
    : (previewToken ? { preview_token: previewToken } : {});

  // Auto-open the claim form + pre-fill the code when the hunter arrives from
  // /claim (which redirects here after resolving the typed code).
  useEffect(() => {
    const codeFromUrl = searchParams.get("code");
    if (codeFromUrl) {
      setClaimForm((f) => ({ ...f, claim_code: codeFromUrl }));
      setShowClaim(true);
    }
  }, [searchParams]);

  const refresh = async () => {
    const { data } = await api.get(`/drops/${id}`, { params: previewParams });
    setDrop(data.drop);
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Track participation for Loot Passport when viewing a live drop (skip in preview).
  useEffect(() => {
    if (!isPreview && user && drop?.status === "live" && drop?.id) {
      api.post(`/drops/${drop.id}/view`).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, drop?.status, drop?.id]);

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Log in to submit a find.");
      return;
    }
    setClaiming(true);
    try {
      const res = await api.post(
        `/drops/${id}/claim`,
        {
          claim_code: claimForm.claim_code,
          photo_url: claimForm.photo_url || null,
          selfie_url: claimForm.selfie_url || null,
          story: claimForm.story,
          social_handle: claimForm.social_handle,
        },
        { params: previewParams },
      );
      if (res.data?.preview_mode) {
        toast.success("✅ Preview claim succeeded — published drops will mark a real winner.", { duration: 6000 });
        setShowClaim(false);
      } else {
        toast.success("Find submitted! Awaiting admin review.");
        setClaimSubmitted(true);  // keep modal open, swap body to the Holler nudge
      }
      if (res.data?.first_win) {
        try {
          const { firstWinConfetti } = await import("@/lib/confetti");
          firstWinConfetti();
          toast.success("🎉 Your first find! Welcome to the wall.", { duration: 6000 });
        } catch {
          /* noop */
        }
      }
      if (!res.data?.preview_mode) refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  if (!drop) return <div className="py-20 text-center text-[#F5F2E8]/60">Loading drop...</div>;

  const statusColor = drop.status === "live" ? "#E53935" : drop.status === "found" ? "#D7A62A" : "#76B82A";

  return (
    <div className="py-8 space-y-8">
      <OGMeta
        title={`${drop.title} · $${drop.amount} drop · The Local Loot`}
        description={`Cash drop live in ${drop.city}. Follow the clues, find the loot.`}
        image={`${process.env.REACT_APP_BACKEND_URL}/api/og/drop/${drop.id}.png`}
        url={typeof window !== "undefined" ? window.location.href : undefined}
      />
      <Link to="/home" className="font-mono text-xs text-[#F5F2E8]/50 hover:text-[#76B82A]">← Back</Link>

      {(isPreview || drop.preview_mode) && (
        <div
          className="border-2 border-dashed p-3 flex items-start gap-3 text-sm"
          style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.08)" }}
          data-testid="preview-banner"
        >
          <span className="font-mono text-[10px] tracking-widest px-2 py-0.5 shrink-0"
            style={{ background: "#D7A62A", color: "#0F0F0F" }}>
            {previewToken ? "SPONSOR PREVIEW" : "PREVIEW"}
          </span>
          <div>
            <div className="font-display uppercase text-base leading-tight">
              {previewToken
                ? "Preview link — this is what hunters will see"
                : "You're viewing a draft drop as a hunter would"}
            </div>
            <div className="text-xs text-[#F5F2E8]/70 mt-1">
              {previewToken
                ? "Hunters can't find this URL. Clue unlock and warmer/colder hints all work here. The actual hunt goes live once the admin publishes it — that's when notifications fire and the public can claim."
                : "Hunters can't see this URL. Clue unlock, GPS hints, and the claim modal all work — but claiming is a dry-run (no winner record, no notification). Click Publish in the admin Drops tab when you're satisfied."}
            </div>
          </div>
        </div>
      )}

      <div
        className="loot-card-flat p-6 md:p-10 relative overflow-hidden"
        style={drop.is_golden ? {
          borderColor: "#D7A62A",
          background: "linear-gradient(135deg, rgba(215,166,42,0.10), rgba(15,15,15,1) 60%)",
        } : undefined}
      >
        {drop.is_golden && (
          <Crown
            size={200}
            aria-hidden="true"
            className="absolute -right-10 -top-10 opacity-10"
            style={{ color: "#D7A62A" }}
          />
        )}
        <div className="flex items-center gap-3 mb-4 relative">
          {drop.is_golden && (
            <span
              className="tag-pill flex items-center gap-1"
              style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
              data-testid="golden-badge"
            >
              <Crown size={10} /> Golden Drop
            </span>
          )}
          <span className="tag-pill" style={{ color: statusColor, borderColor: statusColor }}>
            {drop.status === "live" && <span className="live-dot inline-block mr-2 align-middle" />}
            {drop.status}
          </span>
          <span className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1">
            <MapPin size={12} />{" "}
            <Link to={`/hunts/${encodeURIComponent(drop.city)}`} className="hover:text-[#76B82A]" data-testid="drop-city-link">
              {drop.city}
            </Link>
          </span>
          {drop.boosted && (
            <span
              className="tag-pill text-[10px]"
              style={{ background: "#E53935", color: "#F5F2E8", borderColor: "#E53935" }}
              data-testid="drop-boosted"
            >
              ⚡ BOOSTED
            </span>
          )}
        </div>
        <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
          {drop.title}
        </h1>
        {drop.status === "live" && (
          <div className="mt-3">
            <NextClueChip drop={drop} size="lg" />
          </div>
        )}
        {drop.status !== "found" && drop.status !== "closed" && (
          <BoostButton drop={drop} />
        )}
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="loot-card-flat p-4">
            <div className="overline">Prize</div>
            <div className="font-display text-3xl mt-1" style={{ color: prizeMeta(drop).color }}>
              {drop.mystery_amount
                ? "Mystery Loot"
                : drop.prize_type === "cash" || !drop.prize_type
                ? `$${drop.amount}`
                : prizeMeta(drop).label}
            </div>
            {drop.prize_custom_label && drop.prize_type !== "cash" && (
              <div className="font-mono text-xs text-[#F5F2E8]/70 mt-1">
                {drop.prize_custom_label}
                {Number(drop.amount) > 0 && (
                  <span className="text-[#F5F2E8]/40"> · ~${drop.amount} value</span>
                )}
              </div>
            )}
            <div className="mt-2">
              <PrizeTypeBadge drop={drop} size="sm" />
            </div>
          </div>
          <div className="loot-card-flat p-4">
            <div className="overline">Sponsor</div>
            <div className="font-display uppercase mt-1 text-lg">
              {drop.sponsor ? drop.sponsor.business_name : "The Local Loot"}
            </div>
          </div>
          <div className="loot-card-flat p-4">
            <div className="overline">Zone</div>
            <div className="font-mono text-sm mt-1 text-[#F5F2E8]/70">
              {drop.zone_description || "Stay alert, in city limits"}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <HuntZoneMap
            zone={drop.zone}
            height={240}
            label="Hunt zone"
            sub={
              drop.zone
                ? "City-level preview · exact spot stays secret"
                : drop.zone_description || "Blurred until live"
            }
            tint={drop.is_golden ? "#D7A62A" : "#76B82A"}
          />
        </div>

        {drop.status === "live" && (
          <button
            onClick={() => setShowClaim(true)}
            data-testid="found-it-btn"
            className="mt-8 btn-loot inline-flex items-center gap-2"
          >
            <Flame size={16} /> Found it?
          </button>
        )}

        {drop.status === "live" && drop.gps_hints_available && (user || previewToken) && (
          <GpsHintButton dropId={drop.id} preview={isAdminPreview} previewToken={previewToken} />
        )}
        {drop.status === "live" && drop.gps_hints_available && !user && !previewToken && (
          <div className="mt-6 loot-card-flat p-4 text-sm text-[#F5F2E8]/70">
            <Link to="/login" className="text-[#D7A62A] underline">Log in</Link> to use the warmer/colder hint on this drop.
          </div>
        )}
      </div>

      {/* Clues */}
      <section>
        <div className="overline mb-3">Clue timeline</div>
        <div className="space-y-3">
          {(drop.clues || []).map((c, i) => (
            <ClueRow key={i} clue={c} idx={i} />
          ))}
          {(!drop.clues || drop.clues.length === 0) && (
            <div className="p-6 text-[#F5F2E8]/50 font-mono text-sm">No clues yet.</div>
          )}
        </div>
      </section>

      {/* Claim modal */}
      {showClaim && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          data-testid="claim-modal"
        >
          {claimSubmitted ? (
            <div
              className="max-w-lg w-full space-y-4 max-h-[92vh] overflow-y-auto"
              data-testid="claim-submitted-panel"
            >
              <div className="loot-card-flat p-6" style={{ background: "#0F0F0F" }}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display uppercase text-2xl">
                    <span style={{ color: "#76B82A" }}>Find submitted.</span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => { setShowClaim(false); setClaimSubmitted(false); }}
                    className="text-[#F5F2E8]/60"
                    data-testid="claim-done-close"
                  >✕</button>
                </div>
                <p className="text-sm text-[#F5F2E8]/75">
                  Nice find! We'll review and post you to the Winners Wall soon.
                </p>
              </div>
              <PostClaimHollerNudge
                name={user?.username || user?.display_name || ""}
                city={drop?.city || ""}
                didWin={true}
                context={drop?.title ? `the "${drop.title}" hunt` : "the hunt"}
                onDismiss={() => { setShowClaim(false); setClaimSubmitted(false); }}
              />
            </div>
          ) : (
          <form
            onSubmit={submitClaim}
            className="loot-card-flat p-6 max-w-md w-full"
            style={{ background: "#0F0F0F" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display uppercase text-2xl">You got the loot?</h3>
              <button type="button" onClick={() => setShowClaim(false)} className="text-[#F5F2E8]/60">✕</button>
            </div>
            {previewToken ? (
              <div
                className="border border-dashed p-3 mb-4 text-sm"
                style={{ borderColor: "#D7A62A", color: "#F5F2E8" }}
              >
                <span className="font-mono text-[10px] tracking-widest" style={{ color: "#D7A62A" }}>
                  SPONSOR PREVIEW
                </span>
                <div className="mt-1">
                  This is the modal hunters see when they find your drop. They'll enter the unique code from the envelope, upload a selfie + proof photo, and you'll review it before it hits the Winners Wall.
                </div>
                <div className="mt-3 text-xs text-[#F5F2E8]/60">
                  The actual submit is disabled in sponsor preview. Want any tweaks? Reply to the admin who sent this link.
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#F5F2E8]/70 mb-4">
                Enter the unique code on the envelope, drop a selfie for the Winners Wall, and tag us on socials.
              </p>
            )}
            <input
              className="loot-input font-mono uppercase mb-1"
              placeholder="Unique claim code"
              value={claimForm.claim_code}
              onChange={(e) => setClaimForm({ ...claimForm, claim_code: e.target.value.toUpperCase() })}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              required
              data-testid="claim-code-input"
            />
            <p className="mb-3 text-[11px] font-mono uppercase tracking-widest text-[#F5F2E8]/50">
              UPPER or lower case — both work
            </p>
            <div className="mb-3">
              <div className="overline mb-1 flex items-center gap-1"><Trophy size={10} /> Your selfie — shown on the Winners Wall</div>
              <PhotoUploader
                value={claimForm.selfie_url}
                onChange={(url) => setClaimForm({ ...claimForm, selfie_url: url })}
                testid="claim-selfie"
              />
            </div>
            <div className="mb-3">
              <div className="overline mb-1 flex items-center gap-1"><Camera size={10} /> Proof photo (the cash / envelope) — for admin review</div>
              <PhotoUploader
                value={claimForm.photo_url}
                onChange={(url) => setClaimForm({ ...claimForm, photo_url: url })}
                testid="claim-photo"
              />
            </div>
            <textarea
              className="loot-input mb-3"
              rows={3}
              placeholder="Short story (optional)"
              value={claimForm.story}
              onChange={(e) => setClaimForm({ ...claimForm, story: e.target.value })}
              data-testid="claim-story-input"
            />
            <input
              className="loot-input mb-4"
              placeholder="Social handle (optional)"
              value={claimForm.social_handle}
              onChange={(e) => setClaimForm({ ...claimForm, social_handle: e.target.value })}
              data-testid="claim-social-input"
            />
            <button className="btn-loot w-full" type="submit" disabled={claiming || !!previewToken} data-testid="claim-submit-btn">
              <Trophy size={16} className="inline mr-2" />
              {previewToken ? "Submit disabled (preview)" : (claiming ? "Submitting..." : "Submit find")}
            </button>
          </form>
          )}
        </div>
      )}
    </div>
  );
}

function BoostButton({ drop }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/boost/config");
        setCfg(data);
      } catch {
        /* noop */
      }
    })();
  }, []);
  if (!cfg || !cfg.enabled || drop.boosted) return null;
  const go = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/boost/checkout", {
        drop_id: drop.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Boost unavailable");
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={go}
      disabled={loading}
      className="mt-3 btn-ghost text-xs inline-flex items-center gap-1"
      style={{ borderColor: "#E53935", color: "#E53935" }}
      data-testid="boost-btn"
    >
      <Zap size={12} />
      {loading ? "..." : `⚡ Boost this drop · $${cfg.price} for ${cfg.duration_hours}h`}
    </button>
  );
}
